import { useMediaQuery } from "@mui/material";
import Head from "next/head";
import { useRouter } from "next/router";
import { ReactElement, useEffect } from "react";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";

import InnerLayout from "../../components/innerLayout/InnerLayout";
import Loader from "../../components/Loader";
import DesktopView from "../../components/public-profile/DesktopView";
import MobileView from "../../components/public-profile/MobileView";
import Tabs from "../../constants/tabs";
import { fetchImagesFromUid, fetchUserData } from "../../firebase/user";
import { setSelectedSidebarUser } from "../../redux/createSidebarSlice";
import { setTab } from "../../redux/createTabSlice";

const ProfilePublic = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { uid } = router.query;
  const {
    isLoading: userDataIsLoading,
    error: userDataError,
    data: userData,
  } = useQuery(
    ["userData", uid],
    async () => {
      if (!uid || typeof uid !== "string" || uid.length === 0) {
        return null;
      }
      return await fetchUserData(uid);
    },
    {
      enabled: !!uid && typeof uid === "string" && uid.length > 0,
    }
  );

  const {
    isLoading: imageDataIsLoading,
    error: imageDataError,
    data: imageData,
  } = useQuery(
    ["imageData", uid],
    async () => {
      if (!uid || typeof uid !== "string" || uid.length === 0) {
        return null;
      }
      return await fetchImagesFromUid(uid);
    },
    {
      enabled: !!uid && typeof uid === "string" && uid.length > 0,
    }
  );

  const isMdSize = useMediaQuery("(min-width:900px)");

  useEffect(() => {
    userData && dispatch(setSelectedSidebarUser(userData));
    dispatch(setTab(Tabs.Search));
    return () => {
      dispatch(setSelectedSidebarUser(null));
    };
  }, [userData]);

  if (!userData || !imageData) {
    return <Loader />;
  }
  const userCreatedAt = userData.createdAt && new Date(userData.createdAt);
  return (
    <>
      <Head>
        <title>
          {`${userData.firstName} ${userData.lastName && userData.lastName[0]}${
            userData.tagline ? ": " + userData.tagline : ""
          }`}
        </title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta
          property="og:title"
          content={`${userData.firstName} ${
            userData.lastName && userData.lastName[0]
          }${userData.tagline ? ": " + userData.tagline : ""}`}
          key="title"
        />
        <meta
          name="description"
          content={
            userData.bio +
            " More Pleaze: Private chef experiences where gourmet meets wellness, approachable meets luxury, and all are welcome. Book your chef today!"
          }
        />
        <meta
          name="keywords"
          content={[userData.chefServices, userData.foodPreferences]?.join(",")}
        />
        <meta
          name="author"
          content={`${userData.firstName} ${
            userData.lastName && userData.lastName
          }`}
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://app.morepleaze.com/profile-public/${userData.uid}`}
        />
      </Head>
      {isMdSize ? (
        <DesktopView userData={userData} imageData={imageData} />
      ) : (
        <MobileView userData={userData} imageData={imageData} />
      )}
    </>
  );
};

ProfilePublic.getLayout = function getLayout(component: ReactElement) {
  return <InnerLayout>{component}</InnerLayout>;
};

export default ProfilePublic;
